import { ReactElement, useState } from 'react'
import { FilterAlt, FilterAltOutlined } from '@mui/icons-material'
import {
  Badge,
  Box,
  Button,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'

export interface CommentListFilterSection {
  /** Stable key, also used in option data-cy attributes. */
  key: string
  /** Section heading (e.g. "Plan", "Geschoss"). */
  title: string
  /** Selectable options; a section with no options is skipped. */
  options: { value: string; label: string }[]
  /** Selected values; empty means "all". */
  selected: string[]
  onChange: (next: string[]) => void
}

interface Props {
  /** Filter sections rendered together in a single popover. */
  sections: CommentListFilterSection[]
  /** data-cy prefix for the button, reset and options. */
  dataCy: string
}

/**
 * Multi-section filter for the comments list — one icon button opening a popover that groups several
 * multi-selects (e.g. plan + storey). Mirrors the element list's `StoreyFilterButton` styling.
 */
const CommentListFilterButton = ({ sections, dataCy }: Props): ReactElement => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const open = Boolean(anchorEl)

  // Render only sections that have options, but base active/reset on all sections so a selection in
  // a section that became optionless (e.g. no storeys) still counts and can be cleared.
  const visibleSections = sections.filter(section => section.options.length > 0)
  const isActive = sections.some(section => section.selected.length > 0)

  return (
    <>
      <Tooltip title="Filtern" arrow disableInteractive>
        <Badge color="primary" variant="dot" invisible={!isActive} overlap="circular">
          <Box
            component="button"
            type="button"
            data-cy={`${dataCy}-button`}
            onClick={event => setAnchorEl(event.currentTarget)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              flexShrink: 0,
              cursor: 'pointer',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              background: isActive ? 'rgba(55,132,52,0.10)' : theme.palette.common.white,
              color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
              '&:hover': { background: theme.palette.action.hover },
            }}
          >
            {isActive ? (
              <FilterAlt sx={{ fontSize: 18 }} />
            ) : (
              <FilterAltOutlined sx={{ fontSize: 18 }} />
            )}
          </Box>
        </Badge>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 260, p: 1.5 } } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Filter</Typography>
            <Button
              size="small"
              onClick={() => {
                sections.forEach(section => section.onChange([]))
                setAnchorEl(null)
              }}
              disabled={!isActive}
              data-cy={`${dataCy}-reset`}
              sx={{ fontSize: 11, minWidth: 0, px: 0.75, py: 0.25, textTransform: 'none' }}
            >
              Zurücksetzen
            </Button>
          </Box>

          {visibleSections.map(section => (
            <Box key={section.key} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                {section.title}
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={section.selected}
                onChange={(_event, next: string[]) => section.onChange(next)}
                sx={{ flexWrap: 'wrap', gap: 0.5 }}
              >
                {section.options.map(option => (
                  <ToggleButton
                    key={option.value}
                    value={option.value}
                    data-cy={`${dataCy}-${section.key}-option-${option.value}`}
                    sx={{
                      px: 1,
                      py: 0.25,
                      fontSize: 12,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  )
}

export default CommentListFilterButton
